/*
	Helper functions for shortcutting DOM manipulation
	
	import d from './DOM';
*/
export type sumber = string | number;
export type input = HTMLInputElement;
export type select = HTMLSelectElement;
export type option = HTMLOptionElement;
export type textarea = HTMLTextAreaElement;
export type button = HTMLButtonElement;
export type div = HTMLDivElement;
export type span = HTMLSpanElement;
export type htmlTemplate = HTMLTemplateElement;
export type anchor = HTMLAnchorElement;
export type img = HTMLImageElement;
export type dialog = HTMLDialogElement;
export type DOM = HTMLElement | input | select | textarea | button | div | htmlTemplate | anchor | img | option | dialog;
export type ValueDOM = input | select | textarea;

export default abstract class d {
	private static htCache: { [selector: string]: string } = {};
	
	/**
	 * Retrieve the actual DOM object. No # required for ID
	 * @param  {string} id An ID that represents a DOM element that you wish to access
	 */
	public static get<DOM>(id: string) {
		return (document.getElementById(id) as unknown) as DOM;
	}
	
	/**
	 * QuerySelector over a context
	 * @param  {string} selector A string that represents a CSS Selector Query for the item you wish to access
	 */
	public static qs<DOM>(selector: string, context?: HTMLElement) {
		return ((context ?? document).querySelector(selector) as unknown) as DOM;
	}
	
	/**
	 * QuerySelectorAll over a context
	 * @param  {string} selector A string that represents a CSS Selector Query for the items you wish to access
	 */
	public static qsa<DOM>(selector: string, context?: HTMLElement) {
		return ((context ?? document).querySelectorAll(selector) as unknown) as DOM[];
	}
	
	/**
	 * QuerySelectorAll over a context - Output to Array by default
	 * @param  {string} selector A string that represents a CSS Selector Query for the items you wish to access
	 * @param context If present, limits css selector to context
	 * @returns An array of HTMLElements that you can immediately use to chain functions like filter, etc
	 */
	public static qa<T extends HTMLElement = DOM>(selector: string, context_or_callbackIfNotNull?: HTMLElement | Document | ((domObj: T[]) => void), callbackIfNotNull?: (domObj: T[]) => void) {
		const documentContext = context_or_callbackIfNotNull === undefined || context_or_callbackIfNotNull === null ? document : context_or_callbackIfNotNull;
		
		if (documentContext instanceof HTMLElement || documentContext instanceof Document) {
			const elements = documentContext.querySelectorAll<T>(selector);
			
			if (callbackIfNotNull == null) { return elements == null ? null : Array.from(elements); }
			else { if (elements != null) { callbackIfNotNull(Array.from(elements)); } }
		} else if (typeof context_or_callbackIfNotNull === 'function') {
			const elements = document.querySelectorAll<T>(selector);
			
			if (callbackIfNotNull == null) { return elements == null ? null : Array.from(elements); }
			else { if (elements != null) { context_or_callbackIfNotNull(Array.from(elements)); } }
		}
	}
	
	/**
	 * QuerySelector over a context
	 * @param  {string} selector A string[] that represents a CSS Selectors Query for the item you wish to access
	 * @param context If present, limits css selectors to context
	 */
	public static aqs<T extends HTMLElement = DOM>(selector: string[], context_or_callbackIfNotNull?: HTMLElement | Document | ((domObj: (T | null)[]) => void), callbackIfNotNull?: (domObj: (T | null)[]) => void) {
		const documentContext = context_or_callbackIfNotNull === undefined || context_or_callbackIfNotNull === null ? document : context_or_callbackIfNotNull;
		const domItems: (T | null)[] = [];
		
		if (documentContext instanceof HTMLElement || documentContext instanceof Document) {
			for (const item of selector) {
				domItems.push(documentContext.querySelector<T>(item));
			}
			
			if (callbackIfNotNull == null) { return domItems; }
			else { if (domItems.length > 0 && domItems.filter(i => i != null).length > 0) { callbackIfNotNull(domItems); } }
		} else if (typeof context_or_callbackIfNotNull === 'function') {
			for (const item of selector) {
				domItems.push(document.querySelector<T>(item));
			}
			
			if (context_or_callbackIfNotNull == null) { return domItems; }
			else { if (domItems.length > 0 && domItems.filter(i => i != null).length > 0) { context_or_callbackIfNotNull(domItems); } }
		}
	}
	
	/**
	 * Array QuerySelectorAll over a context
	 * @param  {string} selector A string[] that represents a CSS Selectors Query for the items you wish to access
	 * @param context If present, limits css selectors to context
	 */
	public static aqsa<T extends HTMLElement = DOM>(selector: string[], context_or_callbackIfNotNull?: HTMLElement | Document | ((domObj: T[]) => void), callbackIfNotNull?: (domObj: T[]) => void) {
		const documentContext = context_or_callbackIfNotNull === undefined || context_or_callbackIfNotNull === null ? document : context_or_callbackIfNotNull;
		let domItems: T[] = [];
		
		if (documentContext instanceof HTMLElement || documentContext instanceof Document) {
			for (const item of selector) {
				domItems = Array.from(documentContext.querySelectorAll<T>(item));
			}
			
			if (callbackIfNotNull == null) { return domItems; }
			else { if (domItems.length > 0 && domItems.filter(i => i != null).length > 0) { callbackIfNotNull(domItems); } }
		} else if (typeof context_or_callbackIfNotNull === 'function') {
			for (const item of selector) {
				domItems = Array.from(document.querySelectorAll<T>(item));
			}
			
			if (context_or_callbackIfNotNull == null) { return domItems; }
			else { if (domItems.length > 0 && domItems.filter(i => i != null).length > 0) { context_or_callbackIfNotNull(domItems); } }
		}
	}
	
	/**
	 * Retrieve the innerHTML of an object
	 * @param selector CSS selector
	 * @param context If present, limits css selector to context
	 * @returns A string with the innerHTML or an empty string
	 */
	public static ht(selector: string, context?: HTMLElement) {
		if (d.htCache[selector] != null && d.htCache[selector] != '') { return d.htCache[selector]; }
		d.htCache[selector] = (((context ?? document).querySelector(selector) as unknown) as DOM)?.innerHTML ?? '';
		return d.htCache[selector];
	}
	
	public static flushHT(selector: string) {
		if (d.htCache[selector] != null && d.htCache[selector] != '') { delete d.htCache[selector]; }
	}
	
	/**
	 * Allows you to build an entire HTML DOM element within a single statement using strongly typed HTMLElement objects
	 * @param domElementTagName The HTMLElement tag you're trying to build
	 * @param className The HTMLElements class value
	 * @param childElements An array of child HTMLElements to add to the this tag - allows multi-dimension construction with the one statement
	 * @param attributes The specific attributes to apply to the master element
	 * @param innerHTML Any string content to apply to the master element
	 * @returns A newly constructed DOM element with infinite number of children
	 */
	public static cr<T extends keyof HTMLElementTagNameMap>(domElementTagName: T, className?: string, childElements?: HTMLElement[], attributes?: any, innerHTML?: string) {
		const d = document.createElement<T>(domElementTagName);
		
		if (className != null) { d.className = className; }
		if (childElements != null) { childElements.forEach(c => d.appendChild(c)); }
		if (attributes != null) { Object.keys(attributes).forEach(k => d.setAttribute(k, attributes[k])); }
		if (innerHTML != null) { d.innerHTML = innerHTML; }
		
		return d;
	}
	
	/**
	 * Shortcode to create a document fragment for HTML templating
	 * @returns A document fragment you can start populating
	 */
	public static frag() {
		return document.createDocumentFragment();
	}
	
	/**
	 * Add classes to selectors
	 * @param selector CSS selector
	 * @param classNames An array of classes you wish to add to this selector of element(s)
	 */
	public static cadd(selector: string, classNames: string[]) {
		d.qsa<DOM>(selector).forEach(i => i.classList.add(classNames.join(',')));
	}
	
	/**
	 * Remove classes to selectors
	 * @param selector CSS selector
	 * @param classNames An array of classes you wish to remove to this selector of element(s)
	 */
	public static cremove(selector: string, classNames: string[]) {
		d.qsa<DOM>(selector).forEach(i => i.classList.remove(classNames.join(',')));
	}
	
	/**
	 * Even shorter (document|context).addEventListener() handler. Triggers infinitely
	 * @param eventName Any valid DOMEvent name
	 * @param eventHandler A callback you provide that will be passed in the Event object once called
	 * @param context What to bind the event to.
	 * @param once Boolean - if true, will change this into a SINGLE trigger event and will automatically clean up after itself
	 */
	public static e(eventName: string, eventHandler: (e: any) => void, context: any, once = false) {
		const options: AddEventListenerOptions = {};
		
		if (once == true) {
			options.once = true;
		}
		
		if (!context) {
			console.warn(`d.e() called with a null context.`, eventName, eventHandler);
			return;
		}
		
		if (NodeList.prototype.isPrototypeOf(context)) {
			context.forEach((b: any) => {
				b.removeEventListener(eventName, eventHandler, options);
				b.addEventListener(eventName, eventHandler, options);
			});
		} else {
			context.removeEventListener(eventName, eventHandler, options);
			context.addEventListener(eventName, eventHandler, options);
		}
	}
	
	/**
	 * Even shorter (document|context).addEventListener() handler. Triggers once only
	 * @param eventName Any valid DOMEvent name
	 * @param eventHandler A callback you provide that will be passed in the Event object once called
	 * @param context If provided, uses the HTMLElement as the context, otherwise will use document as the context
	 */
	public static e1(eventName: string, eventHandler: (e: any) => void, context?: any) {
		d.e(eventName, eventHandler, context, true);
	}
	
	public static closest(sourceElement: Element | HTMLElement | EventTarget, selector: string): HTMLElement | null {
		return (<HTMLElement>sourceElement).closest(selector);
	}
	
	/**
	 * A shortcut method to select option items within an HTMLSelectElement whilst being able to populate the select at the same time.
	 * @param selector Either a css selector string or an actual HTMLElement
	 * @param selectedValues An array of values to select (ie. the EXACT STATE) for the multi-select
	 * @param populateAsWell An optional array of "new Option()" HTMLOptionElements to populate the select with at the same time
	 * @returns The fully populated select with each HTMLOptionElement selected as specified
	 */
	public static values(selector: string | select, selectedValues: string[], populateAsWell?: HTMLOptionElement[]) {
		let select: select | null = null;
		
		if (typeof selector == 'string') {
			select = d.qs<select>(selector);
		} else {
			select = selector;
		}
		
		if (populateAsWell != null) {
			select.innerHTML = populateAsWell.join(`\n`);
		}
		
		const options: { [key: string]: HTMLOptionElement } = {};
		
		for (let i = 0; i < select.options.length; i++) {
			select.options[i].selected = false;
			options[select.options[i].value] = select.options[i];
		}
		
		for (const item of selectedValues) {
			if (options[item] != null) {
				options[item].selected = true;
			}
		}
		
		return select;
	}
	
	/**
	 * Wrapper methods to quickly add/remove the hidden class from elements
	 */
	public static show(selector: string | HTMLElement) {
		d.toggleClass(selector, 'hidden', false);
	}
	
	public static hide(selector: string | HTMLElement) {
		d.toggleClass(selector, 'hidden', true);
	}
	
	public static toggleClass(selector: string | HTMLElement, className: string, show?: boolean) {
		if (typeof selector == 'string') { selector = d.qs<DOM>(selector); }
		selector?.classList.toggle(className, show);
	}
	
	public static action<T extends HTMLElement = DOM>(sourceElement: Element | HTMLElement | EventTarget, selector: string, callbackIfNotNull?: (action: string) => void) {
		if (callbackIfNotNull == null) {
			return ((<HTMLElement>sourceElement).closest<T>(selector))?.dataset.action ?? null;
		} else {
			const obj = (<HTMLElement>sourceElement).closest<T>(selector);
			if (obj != null) { callbackIfNotNull(obj.dataset.action ?? ''); }
			return null;
		}
	}
	
	/**
	 * A helper designed to reduce the amount of casting between object types to make simple DOM lookups easier
	 * @param e The Event object created by javascript
	 * @param selector A string that represents what item you're looking to be returned
	 * @param callbackIfNotNull The callback that will send you the found DOM object in the type you provide (or HTMLElement by default)
	 */
	public static btn<T extends HTMLElement = DOM>(e: Event, selector: string, callbackIfNotNull: (btn: T) => void) {
		const domObj = (<HTMLElement>e.target).closest<T>(selector);
		if (domObj != null) { callbackIfNotNull(domObj); }
	}
}
